import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { hasModuleAccess, hasModuleViewAccess } from '@/lib/module-permissions';
import { hubApps } from '../../config/apps';
import { useUserPermissions } from '../../hooks/useUserPermissions';

interface AppProtectedRouteProps {
  children: ReactNode;
  /** Hub module slug ({module}:view or {module}:admin required). */
  module: string;
  /** The name of the app to check permissions for */
  appName: string;
}

export function AppProtectedRoute({ children, module, appName = '' }: AppProtectedRouteProps) {
  const { hasPermission, isLoading } = useUserPermissions();
  const navigate = useNavigate();
  if (isLoading) {
    return <Loader text="Checking permissions..." />;
  }

  const app = hubApps.find((app) => app.id === `app_${appName}`);
  const hasAppPermission = hasModuleAccess(hasPermission, module);

  const isComingSoon = app?.comingSoon || false;
  const hasComingSoonPermission = hasModuleViewAccess(hasPermission, 'coming-soon');

  const hasFullAccess = hasAppPermission && (hasComingSoonPermission || !isComingSoon);

  if (!hasFullAccess) {
    return (
      <div className="flex h-(--page-main-height) items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="error">
                <Lock />
              </EmptyMedia>
              <EmptyTitle>Access denied</EmptyTitle>
              <Button variant="default" className="mt-4" onClick={() => navigate('/apps')}>
                Back to apps
              </Button>
            </EmptyHeader>
          </Empty>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
